import { ChartAdapterProvider } from '@clockwise/report-kit';
import { rechartsAdapter } from '@clockwise/report-kit/recharts';
import { ApiError } from '@clockwise/shared';
import {
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppToaster } from './components/custom/app-toaster';
import { RouteError, RoutePending } from './components/custom/route-status';
import { ThemeProvider } from './components/theme-provider';
import { TooltipProvider } from './components/ui/tooltip';
import { getSession } from './lib/auth-client';
import { rememberSearches } from './lib/last-search';
import { initLogRocket } from './lib/logrocket';
import { queryKeys } from './lib/query-keys';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';

initLogRocket();

// Global handler for UNAUTHENTICATED query/mutation errors. A 401 can mean two
// different things, so we re-check the session before deciding where to send
// the user (module-level flag collapses N parallel failures into one redirect;
// hard reload resets cache, in-flight requests, and stale UI cleanly):
//   - session genuinely gone → /login (?next= preserves the current path).
//   - session still valid but the request was unauthorized → the account is
//     unprovisioned/blocked → /no-access. Bouncing these to /login would loop,
//     because the still-valid session sends them right back in.
const ME_QUERY_KEY = JSON.stringify(queryKeys.users.me());

let redirecting = false;
async function handleUnauthenticated(
  err: unknown,
  query?: Query<unknown, unknown, unknown, readonly unknown[]>,
): Promise<void> {
  if (redirecting) return;
  if (!(err instanceof ApiError) || err.code !== 'UNAUTHENTICATED') return;
  const path = window.location.pathname;
  if (path === '/login' || path === '/no-access') return;
  // The `me` query is owned by the route boundary (`ensureProvisionedMe`),
  // which already does this session-aware redirect. Skip it here to avoid a
  // duplicate/racing navigation.
  if (query && JSON.stringify(query.queryKey) === ME_QUERY_KEY) return;

  redirecting = true;
  const fresh = await getSession().catch(() => null);
  if (fresh?.data) {
    // Valid session, unauthorized request → unprovisioned/blocked account.
    window.location.assign('/no-access');
  } else {
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (err, query) => void handleUnauthenticated(err, query) }),
  mutationCache: new MutationCache({ onError: (err) => void handleUnauthenticated(err) }),
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError) && count < 2,
      // Treat data as fresh for 30s. Without this, every route mount refetches
      // even when the user is just bouncing between pages they just left.
      // Mutations explicitly invalidate via queryKey on success.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Show a spinner (not a blank screen) once a route's beforeLoad/loader —
  // e.g. the cold-cache session + `me` fetch on the landing route — runs past
  // 300ms, and a retryable error screen when one throws.
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 300,
  defaultErrorComponent: RouteError,
});

// Lets a detail page's "Back to …" link return to the list as the user left
// it — search, filters, sort, page — instead of the bare, unfiltered list.
rememberSearches(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* WHICH LIBRARY DRAWS CHARTS — declared once, for the whole app.

              `@clockwise/report-kit` deliberately imports no charting library
              of its own; it renders a neutral ChartSpec and asks whoever
              mounted it to draw. That is what keeps swapping Recharts for
              something else a one-line change here plus a sibling adapter,
              rather than an edit to every chart in the product.

              At the ROOT rather than at each chart, so the report builder's
              preview and a placed dashboard tile cannot end up drawing through
              different renderers — which is the drift `ReportView` exists to
              prevent. */}
          <ChartAdapterProvider adapter={rechartsAdapter}>
            <RouterProvider router={router} />
          </ChartAdapterProvider>
          <AppToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
